import { DatePipe } from '@angular/common';
import { Component, HostListener, OnInit, Pipe, PipeTransform, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, timeout } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { InvoiceCacheService } from '../../services/invoice-cache.service';
import { InvoiceService } from '../../services/invoice.service';
import { FacilityService } from '../../services/facility.service';
import { PatientService } from '../../services/patient.service';
import { SharedService } from '../../services/shared.service';
import { formatOopsErrorMessage, validateDecimalInput } from '../../shared-data/helper-functions';
/** Invoice review status IDs (match backend). */
const REVIEW_STATUS = { PENDING: 1, FLAGGED: 2, APPROVED: 3 } as const;

/** InvoicePaymentStatus IDs (match dbo.InvoicePaymentStatus). */
const PAYMENT_STATUS = {
  UNPAID: 1,
  PARTIALLY_PAID: 2,
  PAID: 3,
  PARTIALLY_REFUNDED: 4,
  REFUNDED: 5
} as const;

/** One row in the Invoice and Kroll amount comparison table (matched by Rx # + fill date). */
export interface InvoiceKrollCompareRow {
  matchKey: string;
  fillDate: any;
  origRxNum: number;
  rxNum: number;
  quantity: number;
  drug: string;
  din: string;
  cost: number;
  fee: number;
  total: number;
  subPlanCode: string;
  taxable: boolean;
  invoiceTaxable: boolean | null;
  krollTaxable: boolean | null;
  taxableDiffers: boolean;
  reason: string;
  invoicePays: number | null;
  krollPays: number | null;
  /** Ontario HST (13%) on Kroll Pays when taxable; null when no Kroll Pays. */
  krollHst: number | null;
  /** Ontario HST (13%) on Invoice Pays when taxable; null when no Invoice Pays. */
  invoiceHst: number | null;
  paysDiff: number | null;
  hstDiff: number | null;
  paysMatch: boolean;
  hstMatch: boolean;
  inInvoiceOnly: boolean;
  inKrollOnly: boolean;
  costDiffers: boolean;
  feeDiffers: boolean;
  totalDiffers: boolean;
  krollCost: number | null;
  krollFee: number | null;
  krollTotal: number | null;
}

export interface NormalizationRuleFacility {
  id: number;
  name: string;
}

export interface NormalizationRuleFacilityGroup {
  title: string;
  note: string;
  facilities: NormalizationRuleFacility[];
}

export interface NormalizationRuleDef {
  key: string;
  label: string;
  description: string;
  /** TEMP: Korean help text for this Details panel only. Remove later. */
  descriptionKo: string;
  facilityGroups: NormalizationRuleFacilityGroup[];
}

@Component({
  selector: 'app-invoice-review-details',
  templateUrl: './invoice-review-details.component.html',
  styleUrls: ['./invoice-review-details.component.css'],
})
export class InvoiceReviewDetailsComponent implements OnInit {

  constructor(private sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute,
    private invoiceService: InvoiceService,
    private invoiceCacheService: InvoiceCacheService,
    private facilityService: FacilityService,
    private patientService: PatientService,
    private datepipe: DatePipe) { }

  @ViewChild('RemoveConfirmModalClosebutton', { static: false }) closebutton;

  validateDecimalInput: Function = validateDecimalInput;

  showErrorMessage: boolean;
  errorMessage: string;
  showWarningMessage: boolean = false;
  warningMessage: string = '';
  invoiceKroll: any = {
    invoiceItemDtos: []
  };
  invoice: any = {
    invoiceItemDtos: []
  };
  drugPlans: any[] = [];
  drugPlansLoading: boolean = false;
  invoiceItemPlans: any = [];
  invoiceItem: any = {};
  invoiceItemPlanDtos: any = [];
  searchStartDate: any;
  searchEndDate: any;
  showLoadingMessage: boolean;
  residents: any = [];
  patientId: number;
  rxNum: any;
  invoiceItemPays: number;
  invoiceItemTotal: number;
  invoiceItemFee: number;
  invoiceItemCost: number;
  invoiceItemTaxable: boolean;
  newInvoiceItem: any = {};
  hasIaPlan: boolean;
  errorMessageAddModal: string;
  showErrorMessageAddModal: boolean = false;
  invoiceId: number;
  pharmacyId: number;
  facilityId: number;
  startDate: string;
  isReapplyingNormalization = false;
  isForceCompletingPurchase = false;
  isRefundingPayment = false;
  isRunningScheduledCharge = false;
  forceCompleteEligible = false;
  forceCompletePaymentId: number = null;
  forceCompleteAmount: number = null;
  forceCompleteStatusLoading = false;
  normalizationPanelCollapsed = true;
  expandedNormalizationRuleKey: string = null;
  expandedNormalizationRule: NormalizationRuleDef = null;
  showNormalizationRuleKoHelp = false;
  normalizationPanelLeft: number = null;
  normalizationPanelTop: number = null;
  isDraggingNormalizationPanel = false;
  facilityCopayLoaded = false;
  facilityCopayEnabled = false;
  facilityCopayCharge = 1;
  facilityCopayValue: number = null;
  showScrollToBottomButton = false;
  showScrollToTopButton = false;
  private normalizationDragOffsetX = 0;
  private normalizationDragOffsetY = 0;
  private normalizationDragMoved = false;
  private normalizationFabSuppressClick = false;

  readonly normalizationRuleDefs: NormalizationRuleDef[] = [
    {
      key: 'otcForceTaxableFalse',
      label: 'OTC force Taxable = false',
      description:
        'Runs first.\n' +
        'When: Taxable is true (OTC true or false).\n' +
        'Action: set Taxable to false (HST not applied as taxable).',
      descriptionKo:
        '가장 먼저 적용.\n' +
        '조건: Taxable = true (OTC true/false 무관).\n' +
        '동작: Taxable → false.',
      facilityGroups: []
    },
    {
      key: 'odbCopayCap',
      label: 'ODB Copay Cap (Scenario 1)',
      description:
        'Uses Settings → Facility → Copay for this home.\n' +
        'When Enable Copay is on: keep One time or Two times ODB copay(s) per drug (DIN) at the exact Kroll amount; ' +
        'waive any extra (Pays = $0). Only when Pays equals Copay Value exactly (e.g. $6.11).\n' +
        'When Enable Copay is off: bill every ODB copay as Kroll shows — no waive.\n' +
        'Example (Two times, $6.11): three $6.11 copays on the same drug → bill 2, waive 1.',
      descriptionKo:
        'Settings → Facility → Copay 설정을 사용합니다.\n' +
        'Enable Copay ON: 약(DIN)당 One time/Two times만 Kroll 금액 그대로 청구, 나머지는 Pays=$0 면제. ' +
        'Pays가 Copay Value와 정확히 같을 때만 대상.\n' +
        'Enable Copay OFF: 면제 없이 전부 청구.\n' +
        '예) Two times + $6.11 ×3 → 2개 청구, 1개 면제.',
      facilityGroups: []
    },
    {
      key: 'privateInsuranceChargeAll',
      label: 'Private Insurance (Scenario 2)',
      description:
        'When: non-ODB private plan covers the claim (and no Cash plan on the line).\n' +
        'If Cash is also present (e.g. ONNMS / Cash), Cash wins → Scenario 3 instead.\n' +
        'Action: charge full Kroll Patient Pays. Do not waive.',
      descriptionKo:
        '조건: ODB가 아닌 민간보험 (라인에 Cash 없음).\n' +
        'Cash가 함께 있으면(예: ONNMS / Cash) Cash 우선 → 시나리오 3.\n' +
        '동작: Kroll Patient Pays 전액 청구. 면제 없음.',
      facilityGroups: []
    },
    {
      key: 'cashFeeCap',
      label: 'Cash Dispensing Fee Cap (Scenario 3)',
      description:
        'When: Cash plan is present (including mixed plans like ONNMS / Cash).\n' +
        'OTC: waive dispensing fee when Pays = Cost+Fee or Pays = Fee.\n' +
        'Rx: keep first fee per DIN (exact Kroll); waive later fees the same way.',
      descriptionKo:
        '조건: Cash 플랜이 있음 (ONNMS / Cash처럼 혼합 포함).\n' +
        'OTC: Pays=Cost+Fee 또는 Fee만이이면 조제료 면제.\n' +
        'Rx: DIN당 첫 Fee 유지, 이후 Fee 면제.',
      facilityGroups: []
    },
    {
      key: 'odbDeductibleProtect',
      label: 'ODB Deductible Protect (Scenario 4)',
      description:
        'When: ODB deductible.\n' +
        'Action: charge full Patient Pays. Never waive (even with duplicate billing error).',
      descriptionKo:
        '조건: ODB Deductible.\n' +
        '동작: Patient Pays 전액 청구. 면제 금지.',
      facilityGroups: []
    },
    {
      key: 'brandGenericProtect',
      label: 'Brand / Generic Differential (Scenario 5)',
      description:
        'When: messages contain \"Reduced to generic cost\".\n' +
        'Action: charge full Patient Pays including brand/generic differential.',
      descriptionKo:
        '조건: \"Reduced to generic cost\".\n' +
        '동작: Patient Pays 전액 청구.',
      facilityGroups: []
    },
    {
      key: 'residentLowIncome',
      label: 'Resident Low Income',
      description:
        'When: drug plan approved and Pays equals $2.00, and this facility is not on the NH exception list for the invoice start date.\n' +
        'Action: set Pays to $0.00 (Resident Low Income).',
      descriptionKo:
        '조건: DrugPlanApproved + Pays=$2.00, NH 예외 Facility가 아님.\n' +
        '동작: Pays → $0.',
      facilityGroups: [
        {
          title: 'Skipped for NH exception (from 2024-03-01)',
          note: 'West Hill also requires start date before 2024-11-01.',
          facilities: [
            { id: 29, name: 'West Hill Retirement' },
            { id: 31, name: 'Royal Oak Senior Living' },
            { id: 32, name: 'Caroline Place Retirement Residence' },
            { id: 33, name: 'Aberdeen Gardens' },
            { id: 36, name: 'Christie Oaks Care Home' }
          ]
        },
        {
          title: 'Skipped for NH exception (from 2024-10-01)',
          note: 'Invoice start date on or after 2024-10-01.',
          facilities: [
            { id: 50, name: 'Atrium Retirement Residence' },
            { id: 41, name: 'Bethany Care Home' },
            { id: 39, name: 'Devonshire Retirement Residence' },
            { id: 40, name: 'Lifetimes On Riverside' },
            { id: 45, name: 'Maple Court Retirement Residence' },
            { id: 51, name: 'Mayfield Retirement Residence' },
            { id: 46, name: 'Oxford Manor Retirement Residence' },
            { id: 48, name: 'Park Place Retirement' },
            { id: 42, name: 'Peterborough Retirement Residence' },
            { id: 38, name: 'Sandytown Retirement Home' },
            { id: 30, name: 'St Jacobs Place' },
            { id: 49, name: 'The Manor At Gravenhurst' },
            { id: 43, name: 'The Millwood Retirement Home' },
            { id: 44, name: 'The Teddington Retirement Residence' },
            { id: 47, name: 'Tillsonburg Retirement Residence' },
            { id: 52, name: 'Willoughby Manor Retirement Residence' },
            { id: 58, name: 'Winchester Glen Retirement' }
          ]
        }
      ]
    },
    {
      key: 'nhZeroPaysDrugGroups',
      label: 'NH zero Pays drug groups',
      description:
        'When: facility is on a list below and the line drug group matches.\n' +
        'Vitamin C/D/Calcium or Families1st → set Pays to $0.00.',
      descriptionKo:
        '조건: 아래 Facility + Vitamin C/D/Calcium 또는 Families1st 그룹.\n' +
        '동작: Pays → $0.',
      facilityGroups: [
        {
          title: 'Vitamin C / D / Calcium — Pays → $0',
          note: 'Drug group includes Vitamin C/D/Calcium.',
          facilities: [
            { id: 61, name: 'West Perth Village' },
            { id: 55, name: 'Bobier Villa' },
            { id: 35, name: 'Craigwiel Gardens' },
            { id: 54, name: 'Elgin Manor' },
            { id: 57, name: 'Huronlea Home for the Aged' },
            { id: 56, name: 'Huronview' },
            { id: 53, name: 'Terrace Lodge' }
          ]
        },
        {
          title: 'Families1st — Pays → $0',
          note: 'Drug group includes Families1st.',
          facilities: [
            { id: 62, name: 'FF Coldstream' },
            { id: 63, name: 'FF Iris' },
            { id: 64, name: 'FF Bandsman' },
            { id: 65, name: 'FF Douglas' },
            { id: 66, name: 'FF Thornton' },
            { id: 67, name: 'FF Hopkinson' },
            { id: 68, name: 'FF Beaverdams' },
            { id: 69, name: 'FF Pontiac' }
          ]
        }
      ]
    }
  ];

  ngOnInit() {
    this.sharedService.reloadUserPrivileges();
    this.route.params.subscribe(params => {
      this.invoiceId = +params['invoiceId'];
      this.pharmacyId = +params['pharmacyId'];
      this.facilityId = +params['facilityId'];
      this.patientId = +params['patientId'];
      this.startDate = params['startDate'];

      if (isNaN(this.invoiceId)
        || isNaN(this.pharmacyId)
        || isNaN(this.facilityId)
        || isNaN(this.patientId)
        || !this.startDate) {
        this.router.navigate(['invoice/review']);
        return;
      }

      this.loadFields();
    });
  }

  /** Returns Swal config for status change actions. */
  private getStatusChangeConfig(status: number, isForcePending: boolean = false): any {
    const patientName = (this.invoice && this.invoice.billedTo) ? this.invoice.billedTo : 'Unknown';
    const configs = {
      1: { // Pending (Cancel Approval)
        icon: 'warning' as const,
        title: isForcePending ? 'Force Pending (Dangerous)' : 'Cancel Approval',
        html: isForcePending
          ? `<p>You are about to <strong class="text-danger">force</strong> the invoice back to <strong>Pending</strong> status.</p>
             <p>This will allow updating the Sage invoice for <strong>${patientName}</strong>.</p>
             <p class="text-danger mb-0"><strong>⚠ Use with caution:</strong> Only for Sage update workflow.</p>`
          : `<p>Are you sure you want to cancel the approval on <strong>${patientName}</strong> invoice and set it to <strong>Pending</strong>?</p>`,
        confirmButtonText: isForcePending ? 'Yes, force to Pending' : 'Yes, cancel approval',
        confirmButtonColor: isForcePending ? '#b91c1c' : '#d33'
      },
      2: { // Flagged
        icon: 'warning' as const,
        title: 'Set Invoice Status to Flagged',
        html: `<p>Are you sure you want to apply the status <strong>Flagged</strong> on <strong>${patientName}</strong> invoice?</p>`,
        confirmButtonText: 'Yes, flag it',
        confirmButtonColor: '#dc3545'
      },
      3: { // Approved
        icon: 'question' as const,
        title: 'Set Invoice Status to Approved',
        html: `<p>Are you sure you want to apply the status <strong>Approved</strong> on <strong>${patientName}</strong> invoice?</p>`,
        confirmButtonText: 'Yes, approve it',
        confirmButtonColor: '#28a745'
      }
    };
    return configs[status] || configs[1];
  }

  /** True when invoice is Approved; blocks Flag/Approve actions but allows Cancel Approval. */
  get disableStatusButton(): boolean {
    return this.invoice.invoiceReviewStatusId === REVIEW_STATUS.APPROVED;
  }

  /** Privilege.InvoiceCreate — shows Force Pending (same flow as PENDING). */
  get hasInvoiceCreatePrivilege(): boolean {
    const v = this.sharedService.userPrivilegeVo;
    if (v == null) {
      return false;
    }
    return v.hasInvoiceAccessCreate === true || v.HasInvoiceAccessCreate === true;
  }

  get disableFlagButton(): boolean {
    return !this.invoice
      || this.hasSagePaymentRecord()
      || this.disableStatusButton
      || this.invoice.invoiceReviewStatusId === REVIEW_STATUS.FLAGGED;
  }

  get disableApproveButton(): boolean {
    return !this.invoice || this.hasSagePaymentRecord() || this.disableStatusButton;
  }

  /** Pending (cancel approval): Approved and Sage sales invoice not yet completed. */
  get disablePendingButton(): boolean {
    return !this.invoice
      || this.hasSagePaymentRecord()
      || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.invoice.invoiceSageStatusId >= 3;
  }

  /** Settings → User privilege — Invoice and Kroll amount comparison panel. */
  get hasSettingsUserAccess(): boolean {
    const v = this.sharedService.userPrivilegeVo;
    if (v == null) {
      return false;
    }
    return v.hasSettingsUserAccess === true || v.HasSettingsUserAccess === true;
  }

  get sentToSage(): Boolean {
    return this.invoice.invoiceSageStatusId >= 3;
  }

  /** CREATE: enabled when Approved and Sage not yet completed (< 3). */
  get disableCreateSalesInvoiceButton(): boolean {
    return !this.invoice
      || this.hasSagePaymentRecord()
      || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.invoice.invoiceSageStatusId >= 3;
  }

  /** UPDATE: enabled when Approved and Sage completed (3: Completed, 4: CompletedWithNewUser, 5: Updated). */
  get disableUpdateSalesInvoiceButton(): boolean {
    return !this.invoice
      || this.hasSagePaymentRecord()
      || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.disablePaymentReviewEditButtons
      || (this.invoice.invoiceSageStatusId !== 3 && this.invoice.invoiceSageStatusId !== 4 && this.invoice.invoiceSageStatusId !== 5);
  }

  /**
   * Treat invoiceNumber as a string (coerce + trim).
   * CREATE: null/undefined, empty after trim, or exactly "0".
   * UPDATE: any other non-empty string.
   */
  private hasPersistedInvoiceId(): boolean {
    if (!this.invoice || this.invoice.invoiceNumber == null) {
      return false;
    }
    const s = String(this.invoice.invoiceNumber).trim();
    return s.length > 0 && s !== '0';
  }

  /** No invoice number yet → only Create is shown. */
  get showCreateSalesInvoiceButton(): boolean {
    return !!this.invoice && !this.hasPersistedInvoiceId();
  }

  /** Invoice number exists → only Update is shown. */
  get showUpdateSalesInvoiceButton(): boolean {
    return !!this.invoice && this.hasPersistedInvoiceId();
  }

  /**
   * Force Pending: enabled only when invoice is Approved, Sage flow completed (invoiceSageStatusId >= 3),
   * and has a valid invoice number — allows forcing back to Pending for Sage update workflow.
   */
  get disableForcePendingButton(): boolean {
    return !this.invoice
      || this.hasSagePaymentRecord()
      || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.disablePaymentReviewEditButtons
      || this.invoice.invoiceSageStatusId < 3
      || !this.hasPersistedInvoiceId();
  }

  get inoviceStartDate(): string {
    if (!this.invoice || !this.invoice.startDate) return '';
    return this.transformDate(this.invoice.startDate);
  }

  get inoviceEndDate(): string {
    if (!this.invoice || !this.invoice.endDate) return '';
    return this.transformDate(this.invoice.endDate);
  }

  get hasPreviousInvoice(): boolean {
    const invoice = this.invoiceCacheService.getPreviousInvoice(this.invoiceId, this.pharmacyId, this.facilityId, this.patientId);
    return invoice === null;
  }

  get hasNextInvoice(): boolean {
    const invoice = this.invoiceCacheService.getNextInvoice(this.invoiceId, this.pharmacyId, this.facilityId, this.patientId);
    return invoice === null;
  }

  get invoiceStatus(): string {
    if (!this.invoice || !this.invoice.invoiceReviewStatusId)
      return 'Invoice review <span class="">-</span>';

    switch (this.invoice.invoiceReviewStatusId) {
      case 1:
        return 'Invoice review <span class="text-warning">Pending</span>';
      case 2:
        return 'Invoice review <span class="text-danger">Flagged</span>';
      case 3:
        return 'Invoice review <span class="text-success">Approved</span>';
      default:
        return 'Invoice review <span class="">-</span>';
    }
    
  }

  get paymentReviewStatusId(): number {
    if (!this.invoice || this.invoice.invoicePaymentReviewStatusId == null) {
      return REVIEW_STATUS.PENDING;
    }
    return this.invoice.invoicePaymentReviewStatusId;
  }

  get paymentReviewStatus(): string {
    if (!this.invoice) {
      return 'Payment review <span class="">-</span>';
    }

    switch (this.paymentReviewStatusId) {
      case 1:
        return 'Payment review <span class="text-warning">Pending</span>';
      case 2:
        return 'Payment review <span class="text-danger">Flagged</span>';
      case 3:
        return 'Payment review <span class="text-success">Approved</span>';
      default:
        return 'Payment review <span class="">-</span>';
    }
  }

  /** True when payment review is Approved; blocks Flag/Approve (same as invoice review). */
  get disablePaymentReviewEditButtons(): boolean {
    return this.paymentReviewStatusId === REVIEW_STATUS.APPROVED;
  }

  get disablePaymentFlagButton(): boolean {
    return !this.invoice
      || !this.hasPersistedInvoiceId()
      || this.hasSagePaymentRecord()
      || this.disablePaymentReviewEditButtons
      || this.paymentReviewStatusId === REVIEW_STATUS.FLAGGED;
  }

  get disablePaymentApproveButton(): boolean {
    return !this.invoice
      || !this.hasPersistedInvoiceId()
      || this.hasSagePaymentRecord()
      || this.disablePaymentReviewEditButtons;
  }

  get disablePaymentPendingButton(): boolean {
    return !this.invoice
      || !this.hasPersistedInvoiceId()
      || this.hasSagePaymentRecord()
      || this.paymentReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.getInvoicePaymentStatusId() !== PAYMENT_STATUS.UNPAID;
  }

  /** Admin Force Pending: Approved but Purchase not available (e.g. after payment). */
  get disablePaymentForcePendingButton(): boolean {
    return !this.invoice
      || !this.hasPersistedInvoiceId()
      || this.hasSagePaymentRecord()
      || this.paymentReviewStatusId !== REVIEW_STATUS.APPROVED
      || this.canPurchase;
  }

  get canPurchase(): boolean {
    if (!this.invoice || !this.hasPersistedInvoiceId()) {
      return false;
    }
    if (this.hasSagePaymentRecord()) {
      return false;
    }
    if (this.paymentReviewStatusId !== REVIEW_STATUS.APPROVED) {
      return false;
    }
    // Net Portal payment > 0 blocks another purchase; fully refunded rows do not.
    if (this.invoice.hasPortalPayment === true) {
      return false;
    }
    const paymentStatusId = this.getInvoicePaymentStatusId();
    // Unpaid / Refunded, or Paid with net 0 (stale status after full refund).
    if (paymentStatusId !== PAYMENT_STATUS.UNPAID
      && paymentStatusId !== PAYMENT_STATUS.REFUNDED
      && paymentStatusId !== PAYMENT_STATUS.PAID) {
      return false;
    }
    return true;
  }

  get disablePurchaseButton(): boolean {
    return !this.canPurchase;
  }

  /** Privilege.InvoiceAdmin — Admin recovery actions on invoice detail. */
  get hasInvoiceAdminPrivilege(): boolean {
    const v = this.sharedService && this.sharedService.userPrivilegeVo;
    if (!v) {
      return false;
    }
    return v.hasInvoiceAccessAdmin === true || v.HasInvoiceAccessAdmin === true;
  }

  get disableForceCompletePaymentButton(): boolean {
    return this.isForceCompletingPurchase
      || !this.invoice
      || !this.hasPersistedInvoiceId();
  }

  get disableRunScheduledChargeButton(): boolean {
    return this.isRunningScheduledCharge
      || !this.invoice
      || !this.hasPersistedInvoiceId();
  }

  get disableAdminRefundPaymentButton(): boolean {
    return this.isRefundingPayment
      || !this.invoice
      || !this.hasPersistedInvoiceId()
      || this.hasSagePaymentRecord()
      || !this.canAdminRefundPayment();
  }

  /**
   * Helcim / SolvoPayment refund still needed when Portal net > 0,
   * or after Force Pending (Unpaid + payment rows, portal-local balancing only).
   */
  private canAdminRefundPayment(): boolean {
    if (!this.invoice) {
      return false;
    }
    if (this.invoice.hasPortalPayment === true) {
      return true;
    }
    const paid = this.invoice.paymentAmount != null ? Number(this.invoice.paymentAmount) : 0;
    return paid > 0.009 && this.getInvoicePaymentStatusId() === PAYMENT_STATUS.UNPAID;
  }

  getPaymentStatusLabel(): string {
    if (this.invoice && this.invoice.paymentStatus) {
      return this.invoice.paymentStatus;
    }
    switch (this.getInvoicePaymentStatusId()) {
      case PAYMENT_STATUS.PARTIALLY_PAID:
        return 'PartiallyPaid';
      case PAYMENT_STATUS.PAID:
        return 'Paid';
      case PAYMENT_STATUS.PARTIALLY_REFUNDED:
        return 'PartiallyRefunded';
      case PAYMENT_STATUS.REFUNDED:
        return 'Refunded';
      default:
        return 'Unpaid';
    }
  }

  /** Invoice.InvoicePaymentStatusId (1 = Unpaid). Defaults to Unpaid when missing. */
  getInvoicePaymentStatusId(): number {
    if (!this.invoice) {
      return PAYMENT_STATUS.UNPAID;
    }
    const id = this.invoice.invoicePaymentStatusId;
    if (id != null && id > 0) {
      return id;
    }
    return PAYMENT_STATUS.UNPAID;
  }

  /** True when Sage receipt exists: Completed, Updated, or Manual (offline). */
  hasSagePaymentRecord(): boolean {
    if (this.invoice && this.invoice.isOldSagePayment === true) {
      return true;
    }
    const statusId = this.invoice && this.invoice.invoicePaymentSageStatusId != null
      ? Number(this.invoice.invoicePaymentSageStatusId)
      : 0;
    if (statusId === 3 || statusId === 4) {
      return true;
    }
    const label = this.invoice && this.invoice.paymentSageStatus
      ? String(this.invoice.paymentSageStatus).trim()
      : '';
    return label === 'Completed' || label === 'Updated' || label === 'Manual';
  }

  transformDate(date) {
    var dateCoverted = Date.parse(date);
    return this.datepipe.transform(dateCoverted, 'MMMM dd, yyyy');
  }

  openInvoice() {
    this.router.navigate([]).then(result => { window.open('report/invoice/' + this.invoice.id + '/' + 'Invoice', '_blank'); });
  }

  /** Shows SweetAlert2 confirmation for status change (FLAG/APPROVE/PENDING/FORCE PENDING). */
  showStatusChangeConfirm(status: number, isForcePending: boolean = false): void {
    if (!isForcePending) {
      if (status === REVIEW_STATUS.FLAGGED && this.disableFlagButton) {
        return;
      }
      if (status === REVIEW_STATUS.APPROVED && this.disableApproveButton) {
        return;
      }
      if (status === REVIEW_STATUS.PENDING && this.disablePendingButton) {
        return;
      }
    } else {
      if (this.disableForcePendingButton) {
        return;
      }
    }

    const config = this.getStatusChangeConfig(status, isForcePending);

    Swal.fire({
      icon: config.icon,
      title: config.title,
      html: config.html,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: config.confirmButtonText,
      confirmButtonColor: config.confirmButtonColor,
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    }).then(result => {
      if (result.isConfirmed) {
        this.saveUpdateInvoice(status, isForcePending);
      }
    });
  }

  async saveUpdateInvoice(status: number = 0, forcePending: boolean = false): Promise<void> {
    if (this.shouldBlockStatusChange(status)) return;

    if (status > 0) {
      this.invoice.invoiceReviewStatusId = status;
    }

    this.showLoadingMessage = true;
    try {
      const result = await this.invoiceService.saveNewInvoice(
        this.invoice,
        this.sharedService.getUserId(),
        forcePending);
      if (!result.success) {
        this.loadErrorMessage(result.data);
        this.showLoadingMessage = false;
        return Promise.reject();
      }

      if (this.invoice.id === 0) {
        this.applyNewInvoiceResult(result.data);
        this.showLoadingMessage = false;
      } else {
        // Same as Refresh Page — reload invoice + Kroll compare after Invoice actions.
        this.loadFields();
      }
      return Promise.resolve();
    } catch (error) {
      this.loadErrorMessage(error.data);
      this.showLoadingMessage = false;
      return Promise.reject();
    }
  }

  /** Block Flag/Approve when already Approved; allow Cancel Approval (→ Pending). */
  private shouldBlockStatusChange(status: number): boolean {
    return this.disableStatusButton && status !== REVIEW_STATUS.PENDING;
  }

  private applyNewInvoiceResult(data: any): void {
    this.updateRouteValues(data.id);
    this.invoice.id = data.id;
    if (data.invoiceReviewStatusId === REVIEW_STATUS.APPROVED) {
      this.invoice = data;
    }
    this.invoiceId = data.id;
    this.loadFields();
  }

  async onSendUpdateSage(): Promise<void> {
    if (!this.disableStatusButton) return;

    if (this.invoice.id === 0) {
      this.errorMessage = "Please, save the invoice first before sending it to Sage!";
      this.showErrorMessage = true;
      window.scroll(0, 0);
      return Promise.resolve();
    }

    this.showLoadingMessage = true;
    try {
      var result = await this.invoiceService.sendUpdateSage(this.invoice.id, this.sharedService.getUserId());

      if (!result.success) {
        this.loadErrorMessage(result.data);
        this.showLoadingMessage = false;
        return Promise.resolve();
      }

      this.loadFields();
      return Promise.resolve();
    } catch (e) {
      this.loadErrorMessage(e.data);
      this.showLoadingMessage = false;
      return Promise.reject();
    }
  }

  updateRouteValues(newInvoiceId: number) {
    const {
      pharmacyId,
      facilityId,
      patientId,
      startDate
    } = this.route.snapshot.params;

    this.router.navigate([
      '/invoice/review-detail',
      newInvoiceId,
      pharmacyId,
      facilityId,
      patientId,
      startDate
    ]);
  }


  loadErrorMessage(message: any) {
    console.error(message);
    this.showErrorMessage = true;
    this.errorMessage = formatOopsErrorMessage(message);
    window.scroll(0, 0);
  }

  clearErrorMessage() {
    this.showErrorMessage = false;
    this.errorMessage = "";
  }

  clearWarningMessage() {
    this.showWarningMessage = false;
    this.warningMessage = '';
  }

  validateDate(): string[] {
    var errors: string[] = [];
    if (this.getDate(this.searchEndDate) < this.getDate(this.searchStartDate))
      errors.push("The End Date must be greater then Start Date!")

    if (this.patientId == 0)
      errors.push("Please, Select the Resident first.")

    return errors;
  }

  getDate(date): Date {
    date.setHours(0, 0, 0, 0);
    return date;
  }

  reloadNavigation(invoice) {
    this.invoiceId = invoice.id;
    this.patientId = invoice.patientId;
    this.router.navigate([
      'invoice/review-detail',
      invoice.id,
      this.pharmacyId,
      this.facilityId,
      this.patientId,
      this.startDate
    ]);
    this.loadFields();
  }

  goToPharmacyReview(event: Event) {
    if (event) {
      event.preventDefault();
    }
    this.router.navigate(['/invoice/review']);
  }

  onMovePreviousInvoice() {
    const previousInvoice = this.invoiceCacheService.getPreviousInvoice(this.invoiceId, this.pharmacyId, this.facilityId, this.patientId);
    if (previousInvoice) {
      this.reloadNavigation(previousInvoice);
    }
  }

  onMoveNextInvoice() {
    const nextInvoice = this.invoiceCacheService.getNextInvoice(this.invoiceId, this.pharmacyId, this.facilityId, this.patientId);
    if (nextInvoice) {
      this.reloadNavigation(nextInvoice);
    }
  }

  loadFields() {
    this.invoiceKroll = {
      invoiceItemDtos: []
    };
    this.invoice = {
      invoiceItemDtos: []
    };

    this.showLoadingMessage = true;

    // Sibling list for Previous/Next only (not used to restore Pharmacy Review list).
    const invoiceList = this.invoiceCacheService.getInvoices();
    if (!invoiceList || invoiceList.length === 0) {
      this.invoiceService.getNewInvoices(this.pharmacyId, this.facilityId, this.startDate).subscribe(result => {
        if (!result.success) {
          this.loadErrorMessage(result.data);
          return;
        }
        this.invoiceCacheService.setInvoices(result.data);
      });
    }
    
    const kroll$ : Observable<any> = this.invoiceService.getNewKrollInvoice(this.pharmacyId, this.facilityId, this.patientId, this.startDate).pipe(
      catchError(err => {
        this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        return of(null); 
      })
    );

    const invoice$ : Observable<any> = this.invoiceService.getNewPatientInvoice(this.invoiceId, this.pharmacyId, this.facilityId, this.patientId, this.startDate).pipe(
      catchError(err => {
        this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        return of(null); 
      })
    );

    const facility$ : Observable<any[]> = this.facilityService.getFacilityDtosList(this.pharmacyId, false).pipe(
      catchError(err => {
        return of([]);
      })
    );

    const patient$ : Observable<any> = this.patientService.getPatient(this.pharmacyId, this.patientId).pipe(
      catchError(err => {
        return of(null);
      })
    );

    this.drugPlans = [];
    this.drugPlansLoading = true;

    forkJoin([kroll$, invoice$, facility$, patient$]).subscribe(([krollResult, invoiceResult, facilities, patientResult]) => {
      if (krollResult) {
        this.invoiceKroll = krollResult.data;
      }

      if (invoiceResult) {
        this.invoice = invoiceResult.data;
        this.ensureNormalizationOptions();
        if (this.invoice && this.invoice.invoicePaymentReviewStatusId == null) {
          this.invoice.invoicePaymentReviewStatusId = REVIEW_STATUS.PENDING;
        }
      }

      if (patientResult && patientResult.drugPlans) {
        this.drugPlans = patientResult.drugPlans;
      } else if (patientResult && patientResult.DrugPlans) {
        this.drugPlans = patientResult.DrugPlans;
      } else {
        this.drugPlans = [];
      }
      this.drugPlansLoading = false;

      this.applyFacilityCopayFromList(facilities);
      this.rebuildInvoiceKrollCompareRows();
      this.showLoadingMessage = false;
      this.refreshForceCompleteStatus();
      setTimeout(() => this.updateScrollToBottomButtonVisibility(), 0);
    });
  }

  private applyFacilityCopayFromList(facilities: any[]) {
    this.facilityCopayLoaded = false;
    this.facilityCopayEnabled = false;
    this.facilityCopayCharge = 1;
    this.facilityCopayValue = null;

    if (!facilities || !facilities.length) {
      return;
    }

    let match = null;
    for (let i = 0; i < facilities.length; i++) {
      const facility = facilities[i];
      if (facility && facility.id === this.facilityId && facility.pharmacyId === this.pharmacyId) {
        match = facility;
        break;
      }
    }

    if (!match) {
      for (let i = 0; i < facilities.length; i++) {
        const facility = facilities[i];
        if (facility && facility.id === this.facilityId) {
          match = facility;
          break;
        }
      }
    }

    if (!match) {
      return;
    }

    this.facilityCopayLoaded = true;
    this.facilityCopayEnabled = !!match.isCopayEnabled;
    this.facilityCopayCharge = match.copayCharge != null ? match.copayCharge : 1;
    this.facilityCopayValue = match.copayValue != null ? Number(match.copayValue) : null;
  }

  getFacilityCopayEnabledDisplay(): string {
    if (!this.facilityCopayLoaded) {
      return 'Not available';
    }
    return this.facilityCopayEnabled ? 'Enabled' : 'Disabled';
  }

  getFacilityCopayAmountDisplay(): string {
    if (!this.facilityCopayLoaded) {
      return 'Not available';
    }
    if (!this.facilityCopayEnabled) {
      return '—';
    }
    if (this.facilityCopayValue == null) {
      return 'Not set';
    }
    return '$' + Number(this.facilityCopayValue).toFixed(2);
  }

  getFacilityCopayChargeDisplay(): string {
    if (!this.facilityCopayLoaded) {
      return 'Not available';
    }
    if (!this.facilityCopayEnabled) {
      return '— (Copay disabled)';
    }
    if (this.facilityCopayCharge === 1) {
      return 'One time';
    }
    if (this.facilityCopayCharge === 2) {
      return 'Two times';
    }
    return String(this.facilityCopayCharge);
  }

  private createDefaultNormalizationOptions() {
    return {
      odbCopayCap: true,
      privateInsuranceChargeAll: true,
      cashFeeCap: true,
      odbDeductibleProtect: true,
      brandGenericProtect: true,
      residentLowIncome: true,
      nhZeroPaysDrugGroups: true,
      otcForceTaxableFalse: true
    };
  }

  private ensureNormalizationOptions() {
    if (!this.invoice) {
      return;
    }
    if (!this.invoice.normalizationOptions) {
      this.invoice.normalizationOptions = this.createDefaultNormalizationOptions();
      return;
    }
    const defaults = this.createDefaultNormalizationOptions();
    const opts = this.invoice.normalizationOptions;

    // Migrate legacy keys from previous normalization UI.
    if (opts.odbCopayCap == null && opts.secondCopay != null) {
      opts.odbCopayCap = opts.secondCopay;
    }
    if (opts.odbDeductibleProtect == null && opts.odbDeductible != null) {
      opts.odbDeductibleProtect = opts.odbDeductible;
    }

    const keys = Object.keys(defaults);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (opts[key] == null) {
        opts[key] = defaults[key];
      }
    }
  }

  onNormalizationRuleToggle(ruleKey: string, enabled: boolean) {
    if (this.disableStatusButton || this.isReapplyingNormalization) {
      return;
    }
    this.ensureNormalizationOptions();
    this.invoice.normalizationOptions[ruleKey] = enabled;
    this.reapplyNormalizationPreview();
  }

  setAllNormalizationRules(enabled: boolean) {
    if (this.disableStatusButton || this.isReapplyingNormalization) {
      return;
    }
    this.ensureNormalizationOptions();
    for (let i = 0; i < this.normalizationRuleDefs.length; i++) {
      this.invoice.normalizationOptions[this.normalizationRuleDefs[i].key] = enabled;
    }
    this.reapplyNormalizationPreview();
  }

  private reapplyNormalizationPreview() {
    if (!this.invoice) {
      return;
    }
    this.isReapplyingNormalization = true;
    this.invoiceService.reapplyNormalization({
      invoiceId: this.invoiceId || 0,
      pharmacyId: this.pharmacyId,
      facilityId: this.facilityId,
      patientId: this.patientId,
      startDate: this.startDate,
      normalizationOptions: this.invoice.normalizationOptions
    }).pipe(
      finalize(() => {
        this.isReapplyingNormalization = false;
      })
    ).subscribe(result => {
      if (!result || !result.success) {
        this.loadErrorMessage(result && result.data
          ? result.data
          : 'Could not reapply normalization. Please try again.');
        return;
      }
      const data = result.data;
      if (data && data.invoiceItemDtos) {
        this.invoice.invoiceItemDtos = data.invoiceItemDtos;
      }
      if (data && data.normalizationOptions) {
        this.invoice.normalizationOptions = data.normalizationOptions;
      }
      this.loadSummary();
      this.rebuildInvoiceKrollCompareRows();
      this.reapplyActiveSorts();
    }, err => {
      this.loadErrorMessage('Could not reapply normalization. Please try again.');
    });
  }

  saveNormalizationOptions() {
    if (this.disableStatusButton || this.isReapplyingNormalization) {
      return;
    }
    this.saveUpdateInvoice(0);
  }

  toggleNormalizationPanel() {
    this.normalizationPanelCollapsed = !this.normalizationPanelCollapsed;
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onWindowScrollOrResize() {
    this.updateScrollToBottomButtonVisibility();
  }

  scrollToTop() {
    if (window.scrollTo) {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        window.scrollTo(0, 0);
      }
    } else {
      window.scroll(0, 0);
    }
  }

  scrollToBottom() {
    const doc = document.documentElement;
    const body = document.body;
    const scrollHeight = Math.max(
      doc ? doc.scrollHeight : 0,
      body ? body.scrollHeight : 0
    );
    const clientHeight = doc ? doc.clientHeight : (window.innerHeight || 0);
    const scrollTopMax = scrollHeight - clientHeight;
    if (window.scrollTo) {
      try {
        window.scrollTo({ top: scrollTopMax, behavior: 'smooth' });
      } catch (e) {
        window.scrollTo(0, scrollTopMax);
      }
    } else {
      window.scroll(0, scrollTopMax);
    }
  }

  private updateScrollToBottomButtonVisibility() {
    if (!this.invoice || this.showLoadingMessage) {
      this.showScrollToTopButton = false;
      this.showScrollToBottomButton = false;
      return;
    }

    const y = window.pageYOffset
      || (document.documentElement && document.documentElement.scrollTop)
      || (document.body && document.body.scrollTop)
      || 0;
    const winH = window.innerHeight
      || (document.documentElement && document.documentElement.clientHeight)
      || 0;
    const docHeight = Math.max(
      (document.documentElement && document.documentElement.scrollHeight) || 0,
      (document.body && document.body.scrollHeight) || 0
    );
    const canScroll = docHeight > (winH + 80);
    const isNearTop = y <= 200;
    const isNearBottom = (y + winH) >= (docHeight - 200);
    this.showScrollToTopButton = canScroll && !isNearTop;
    this.showScrollToBottomButton = canScroll && !isNearBottom;
  }

  getNormalizationFloatStyle(): { [key: string]: string } {
    if (this.normalizationPanelLeft == null || this.normalizationPanelTop == null) {
      return {};
    }
    return {
      left: this.normalizationPanelLeft + 'px',
      top: this.normalizationPanelTop + 'px',
      right: 'auto',
      bottom: 'auto',
      transform: 'none'
    };
  }

  toggleNormalizationRuleDetails(ruleKey: string) {
    if (this.expandedNormalizationRuleKey === ruleKey) {
      this.expandedNormalizationRuleKey = null;
      this.expandedNormalizationRule = null;
      this.showNormalizationRuleKoHelp = false;
      return;
    }
    this.expandedNormalizationRuleKey = ruleKey;
    this.expandedNormalizationRule = this.findNormalizationRuleDef(ruleKey);
    this.showNormalizationRuleKoHelp = false;
    this.keepNormalizationPanelInViewport();
  }

  private keepNormalizationPanelInViewport() {
    if (this.normalizationPanelLeft == null || this.normalizationPanelTop == null) {
      return;
    }
    const panelWidth = this.expandedNormalizationRuleKey ? 832 : 480;
    const maxLeft = Math.max(0, window.innerWidth - panelWidth);
    if (this.normalizationPanelLeft > maxLeft) {
      this.normalizationPanelLeft = maxLeft;
    }
    if (this.normalizationPanelLeft < 0) {
      this.normalizationPanelLeft = 0;
    }
  }

  toggleNormalizationRuleKoHelp() {
    this.showNormalizationRuleKoHelp = !this.showNormalizationRuleKoHelp;
  }

  isNormalizationRuleDetailsOpen(ruleKey: string): boolean {
    return this.expandedNormalizationRuleKey === ruleKey;
  }

  private findNormalizationRuleDef(ruleKey: string): NormalizationRuleDef {
    if (!ruleKey) {
      return null;
    }
    for (let i = 0; i < this.normalizationRuleDefs.length; i++) {
      if (this.normalizationRuleDefs[i].key === ruleKey) {
        return this.normalizationRuleDefs[i];
      }
    }
    return null;
  }

  isCurrentNormalizationFacility(facilityId: number): boolean {
    return this.facilityId === facilityId;
  }

  onNormalizationFabClick(event: MouseEvent) {
    if (this.normalizationFabSuppressClick) {
      this.normalizationFabSuppressClick = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.toggleNormalizationPanel();
  }

  onNormalizationDragStart(event: MouseEvent) {
    if (event.button !== 0) {
      return;
    }

    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }

    if (this.isNormalizationDragBlockedTarget(target)) {
      return;
    }

    const floatEl = this.getNormalizationFloatElement(target);
    if (!floatEl) {
      return;
    }

    const rect = floatEl.getBoundingClientRect();
    this.isDraggingNormalizationPanel = true;
    this.normalizationDragMoved = false;
    this.normalizationDragOffsetX = event.clientX - rect.left;
    this.normalizationDragOffsetY = event.clientY - rect.top;
    this.normalizationPanelLeft = rect.left;
    this.normalizationPanelTop = rect.top;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onNormalizationDragMove(event: MouseEvent) {
    if (!this.isDraggingNormalizationPanel) {
      return;
    }

    const panelWidth = this.normalizationPanelCollapsed
      ? 88
      : (this.expandedNormalizationRuleKey ? 832 : 480);
    const maxLeft = Math.max(0, window.innerWidth - panelWidth);
    const maxTop = Math.max(0, window.innerHeight - 80);

    let nextLeft = event.clientX - this.normalizationDragOffsetX;
    let nextTop = event.clientY - this.normalizationDragOffsetY;

    if (nextLeft < 0) {
      nextLeft = 0;
    } else if (nextLeft > maxLeft) {
      nextLeft = maxLeft;
    }

    if (nextTop < 0) {
      nextTop = 0;
    } else if (nextTop > maxTop) {
      nextTop = maxTop;
    }

    if (Math.abs(nextLeft - this.normalizationPanelLeft) > 2
      || Math.abs(nextTop - this.normalizationPanelTop) > 2) {
      this.normalizationDragMoved = true;
    }

    this.normalizationPanelLeft = nextLeft;
    this.normalizationPanelTop = nextTop;
  }

  @HostListener('document:mouseup')
  onNormalizationDragEnd() {
    if (!this.isDraggingNormalizationPanel) {
      return;
    }
    this.isDraggingNormalizationPanel = false;
    if (this.normalizationDragMoved && this.normalizationPanelCollapsed) {
      this.normalizationFabSuppressClick = true;
    }
    this.normalizationDragMoved = false;
  }

  private isNormalizationDragBlockedTarget(target: HTMLElement): boolean {
    let el: HTMLElement = target;
    while (el) {
      const tag = (el.tagName || '').toLowerCase();
      if (tag === 'button' || tag === 'a' || tag === 'input' || tag === 'mat-slide-toggle') {
        // Allow drag from the collapsed FAB itself.
        if (el.classList && el.classList.contains('normalization-float__fab')) {
          return false;
        }
        return true;
      }
      if (el.classList && el.classList.contains('mat-slide-toggle')) {
        return true;
      }
      if (el.classList && el.classList.contains('normalization-float')) {
        break;
      }
      el = el.parentElement;
    }
    return false;
  }

  private getNormalizationFloatElement(target: HTMLElement): HTMLElement {
    let el: HTMLElement = target;
    while (el) {
      if (el.classList && el.classList.contains('normalization-float')) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  } 

  public removeEprescriber() {
    this.invoice.invoiceItemDtos = this.invoice.invoiceItemDtos.filter(i => i.rxNum !== this.rxNum);

    this.closebutton.nativeElement.click();
    this.loadSummary();
    this.rebuildInvoiceKrollCompareRows();
  }

  public openDetailsModal(rxNum) {
    this.loadInvoiceItem(rxNum);
    if (!this.invoiceItem) {
      return;
    }
    this.invoiceItemPlanDtos = this.invoiceItem.invoiceItemPlanDtos;
  }

  public formatOtcYesNo(invoiceItem: any): string {
    if (!invoiceItem || invoiceItem.otc == null) {
      return '—';
    }
    return invoiceItem.otc ? 'Yes' : 'No';
  }

  private loadInvoiceItem(rxNum: any) {
    this.rxNum = rxNum;
    this.invoiceItem = this.invoice.invoiceItemDtos.find(i => i.rxNum == this.rxNum);
  }

  public openRemoveModal(rxNum) {
    this.loadInvoiceItem(rxNum);
  }

  public openEditModal(rxNum) {
    this.loadInvoiceItem(rxNum);    
    this.invoiceItemTotal = this.invoiceItem.total;
    this.invoiceItemFee = this.invoiceItem.fee;
    this.invoiceItemCost = this.invoiceItem.cost;
    this.invoiceItemPays = this.invoiceItem.pays;
    this.invoiceItemTaxable = this.invoiceItem.taxable;
  }

  public saveInvoiceItemPays() {
    this.invoiceItem.total = Number(this.invoiceItemTotal);
    this.invoiceItem.fee = Number(this.invoiceItemFee);
    this.invoiceItem.cost = Number(this.invoiceItemCost);
    this.invoiceItem.pays = Number(this.invoiceItemPays);
    this.invoiceItem.taxable = Boolean(this.invoiceItemTaxable);

    this.invoiceItem.reason = "Edited by " + this.sharedService.userPrivilegeVo.fullName;
    this.invoiceItem.reasonCode = 8;

    this.loadSummary();
    this.rebuildInvoiceKrollCompareRows();
  }


  private loadSummary() {
    this.invoice.subTotal = Number(parseFloat(this.subTotal.toString()).toFixed(2));
    this.invoice.subTotalTaxableItems = Number(parseFloat(this.subTotalTaxableItems.toString()).toFixed(2));
    this.invoice.subTotalNonTaxableItems = Number(parseFloat(this.subTotalNonTaxableItems.toString()).toFixed(2));
    this.invoice.hst = Number(parseFloat(this.hst.toString()).toFixed(2));
    this.invoice.total = Number(parseFloat(this.total.toString()).toFixed(2));
  }

  public openAddInvoiceItemModal() {
    if (this.disableStatusButton) return;
    this.newInvoiceItem = {};
  }

  public saveNewInvoiceItem() {
    if (this.disableStatusButton) return;

    if (this.ValidateFieldsNewInvoice()) {
      this.showErrorMessageAddModal = true;
      this.errorMessageAddModal = "All fields are mandatory!";
    } else {
      this.clearErrorMessageAddModal();
      this.newInvoiceItem.reason = "Edited by " + this.sharedService.userPrivilegeVo.fullName;
      this.newInvoiceItem.reasonCode = 8;
      this.invoice.invoiceItemDtos.push(this.newInvoiceItem);
      this.loadSummary();
      this.rebuildInvoiceKrollCompareRows();
      document.getElementById("closeAddModalButton").click();
    }
  }

  private ValidateFieldsNewInvoice() {
    return this.newInvoiceItem.rxNum == null ||
      this.newInvoiceItem.fillDate == null ||
      this.newInvoiceItem.quantity == null ||
      this.newInvoiceItem.drug == null ||
      this.newInvoiceItem.din == null ||
      this.newInvoiceItem.cost == null ||
      this.newInvoiceItem.fee == null ||
      this.newInvoiceItem.total == null ||
      this.newInvoiceItem.pays == null;
  }

  clearErrorMessageAddModal() {
    this.showErrorMessageAddModal = false;
    this.errorMessageAddModal = "";
  }

  get subTotal(): number {
    if (this.invoice.invoiceItemDtos) {
      return this.invoice.invoiceItemDtos.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
    } else {
      return 0;
    }
  }

  get subTotalTaxableItems(): number {
    if (this.invoice.invoiceItemDtos) {
      const taxableItems = this.invoice.invoiceItemDtos.filter(item => Boolean(item.taxable));
      return taxableItems.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
    } else {
      return 0;
    }
  }

  get subTotalNonTaxableItems(): number {
    if (this.invoice.invoiceItemDtos) {
      const nonTaxableItems = this.invoice.invoiceItemDtos.filter(item => !Boolean(item.taxable));
      return nonTaxableItems.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
    } else {
      return 0;
    }
  }

  get hst(): number {
    if (this.invoice.invoiceItemDtos && !this.hasIaPlan) {
      const taxableItems = this.invoice.invoiceItemDtos.filter(item => Boolean(item.taxable));
      const paysSum = taxableItems.reduce((acc, item) => Number(acc) + Number(item.pays), 0);
      return Math.round(Number(paysSum) * 0.13 * 100) / 100;
    } else {
      return 0;
    }
  }

  get total(): number {
    return this.subTotal + this.hst;
  }

  onAmountBlurEdit() {
    this.invoiceItemTotal = Number(this.invoiceItemCost) + Number(this.invoiceItemFee);
  }

  onAmountBlurAdd() {
    this.newInvoiceItem.total = Number(this.newInvoiceItem.cost) + Number(this.newInvoiceItem.fee);
  }

  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  sortColumnKroll: string = '';
  sortDirectionKroll: 'asc' | 'desc' = 'asc';
  sortColumnCompare: string = 'din';
  sortDirectionCompare: 'asc' | 'desc' = 'asc';
  private _invoiceKrollCompareRows: InvoiceKrollCompareRow[] = [];

  get invoiceKrollCompareRows(): InvoiceKrollCompareRow[] {
    return this._invoiceKrollCompareRows;
  }

  get invoiceKrollComparePaysMismatchCount(): number {
    return this._invoiceKrollCompareRows.filter(r => !r.paysMatch).length;
  }

  get invoiceKrollCompareTaxableMismatchCount(): number {
    return this._invoiceKrollCompareRows.filter(r => r.taxableDiffers).length;
  }

  get hasInvoiceKrollCompareMismatchSummary(): boolean {
    return this.invoiceKrollComparePaysMismatchCount > 0
      || this.invoiceKrollCompareTaxableMismatchCount > 0;
  }

  get invoiceKrollCompareMismatchSummary(): string {
    const parts: string[] = [];
    const amountCount = this.invoiceKrollComparePaysMismatchCount;
    const taxableCount = this.invoiceKrollCompareTaxableMismatchCount;
    if (amountCount > 0) {
      parts.push(amountCount + ' amount mismatch(es)');
    }
    if (taxableCount > 0) {
      parts.push(taxableCount + ' taxable change(s)');
    }
    return parts.join(' · ');
  }

  get compareTotalInvoicePays(): number {
    let sum = 0;
    for (let i = 0; i < this._invoiceKrollCompareRows.length; i++) {
      const pays = this._invoiceKrollCompareRows[i].invoicePays;
      if (pays != null) {
        sum += Number(pays);
      }
    }
    return Number(sum.toFixed(2));
  }

  get compareTotalKrollPays(): number {
    let sum = 0;
    for (let i = 0; i < this._invoiceKrollCompareRows.length; i++) {
      const pays = this._invoiceKrollCompareRows[i].krollPays;
      if (pays != null) {
        sum += Number(pays);
      }
    }
    return Number(sum.toFixed(2));
  }

  get compareTotalKrollHst(): number {
    let sum = 0;
    for (let i = 0; i < this._invoiceKrollCompareRows.length; i++) {
      const hst = this._invoiceKrollCompareRows[i].krollHst;
      if (hst != null) {
        sum += Number(hst);
      }
    }
    return Number(sum.toFixed(2));
  }

  get compareTotalInvoiceHst(): number {
    let sum = 0;
    for (let i = 0; i < this._invoiceKrollCompareRows.length; i++) {
      const hst = this._invoiceKrollCompareRows[i].invoiceHst;
      if (hst != null) {
        sum += Number(hst);
      }
    }
    return Number(sum.toFixed(2));
  }

  get compareTotalPaysDiff(): number {
    return Number((this.compareTotalInvoicePays - this.compareTotalKrollPays).toFixed(2));
  }

  get compareTotalHstDiff(): number {
    return Number((this.compareTotalInvoiceHst - this.compareTotalKrollHst).toFixed(2));
  }

  get compareTotalsPaysMatch(): boolean {
    return this.compareAmountsEqual(this.compareTotalInvoicePays, this.compareTotalKrollPays);
  }

  get compareTotalsHstMatch(): boolean {
    return this.compareAmountsEqual(this.compareTotalInvoiceHst, this.compareTotalKrollHst);
  }

  /** Patient amount due from Kroll side (Pays + HST). */
  get compareTotalKrollCharge(): number {
    return Number((this.compareTotalKrollPays + this.compareTotalKrollHst).toFixed(2));
  }

  /** Patient amount due from Invoice side (Pays + HST). */
  get compareTotalInvoiceCharge(): number {
    return Number((this.compareTotalInvoicePays + this.compareTotalInvoiceHst).toFixed(2));
  }

  get compareTotalChargeDiff(): number {
    return Number((this.compareTotalInvoiceCharge - this.compareTotalKrollCharge).toFixed(2));
  }

  get compareTotalsChargeMatch(): boolean {
    return this.compareAmountsEqual(this.compareTotalInvoiceCharge, this.compareTotalKrollCharge);
  }

  /** Truncate drug name for Comparison table (max 100 characters). */
  public truncateCompareDrug(drug: string): string {
    if (!drug) {
      return '';
    }
    const text = String(drug);
    if (text.length <= 100) {
      return text;
    }
    return text.substring(0, 100) + '…';
  }

  public isNonZeroCompareDiff(diff: number | null): boolean {
    if (diff == null) {
      return false;
    }
    return !this.compareAmountsEqual(Number(diff), 0);
  }

  /** True when HST should show as a number (non-null and not zero). */
  public hasDisplayableCompareHst(hst: number | null): boolean {
    if (hst == null) {
      return false;
    }
    return !this.compareAmountsEqual(Number(hst), 0);
  }

  public sort(column: string, table: string = 'invoice') {
    if (table === 'invoice') {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
      this.applySort(this.sortColumn, 'invoice', this.sortDirection);
      return;
    }

    if (table === 'compare') {
      if (this.sortColumnCompare === column) {
        this.sortDirectionCompare = this.sortDirectionCompare === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumnCompare = column;
        this.sortDirectionCompare = 'asc';
      }
      this.applySort(this.sortColumnCompare, 'compare', this.sortDirectionCompare);
      return;
    }

    if (this.sortColumnKroll === column) {
      this.sortDirectionKroll = this.sortDirectionKroll === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumnKroll = column;
      this.sortDirectionKroll = 'asc';
    }
    this.applySort(this.sortColumnKroll, 'kroll', this.sortDirectionKroll);
  }

  /** Re-apply current column/direction after data refresh (e.g. normalization preview). */
  private reapplyActiveSorts() {
    if (this.sortColumn) {
      this.applySort(this.sortColumn, 'invoice', this.sortDirection);
    }
    if (this.sortColumnKroll) {
      this.applySort(this.sortColumnKroll, 'kroll', this.sortDirectionKroll);
    }
    if (this.sortColumnCompare) {
      this.applySort(this.sortColumnCompare, 'compare', this.sortDirectionCompare);
    }
  }

  private applySort(column: string, table: string, direction: 'asc' | 'desc') {
    let data = null;

    if (table === 'invoice') {
      data = this.invoice && this.invoice.invoiceItemDtos ? this.invoice.invoiceItemDtos : null;
    } else if (table === 'compare') {
      data = this._invoiceKrollCompareRows;
    } else {
      data = this.invoiceKroll && this.invoiceKroll.invoiceItemDtos
        ? this.invoiceKroll.invoiceItemDtos
        : null;
    }

    if (!data || !column) {
      return;
    }

    data.sort((a, b) => {
      let valA = a[column];
      let valB = b[column];

      if (table === 'compare' && column === 'taxable') {
        valA = a.taxable ? 1 : 0;
        valB = b.taxable ? 1 : 0;
      } else if ((table === 'invoice' || table === 'kroll') && column === 'taxable') {
        valA = a.taxable ? 1 : 0;
        valB = b.taxable ? 1 : 0;
      } else if ((table === 'invoice' || table === 'kroll') && column === 'hst') {
        valA = this.resolveOntarioHst(a.pays != null ? Number(a.pays) : null, a.taxable != null ? Boolean(a.taxable) : null);
        valB = this.resolveOntarioHst(b.pays != null ? Number(b.pays) : null, b.taxable != null ? Boolean(b.taxable) : null);
      }

      // Handle null/undefined
      if (valA == null) valA = '';
      if (valB == null) valB = '';

      let res = 0;
      if (typeof valA === 'number' && typeof valB === 'number') {
        res = valA - valB;
      } else if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        res = (valA === valB) ? 0 : (valA ? 1 : -1);
      } else {
        // Simple string comparison for everything else (including dates as strings)
        res = valA.toString().localeCompare(valB.toString(), undefined, { numeric: true, sensitivity: 'base' });
      }

      return direction === 'asc' ? res : -res;
    });

    if (table === 'compare') {
      this._invoiceKrollCompareRows = data;
    }
  }

  private rebuildInvoiceKrollCompareRows(): void {
    if (!this.hasSettingsUserAccess) {
      this._invoiceKrollCompareRows = [];
      return;
    }
    const invoiceItems = (this.invoice && this.invoice.invoiceItemDtos) ? this.invoice.invoiceItemDtos : [];
    const krollItems = (this.invoiceKroll && this.invoiceKroll.invoiceItemDtos) ? this.invoiceKroll.invoiceItemDtos : [];
    const krollByKey: { [key: string]: any[] } = {};

    for (let i = 0; i < krollItems.length; i++) {
      const item = krollItems[i];
      const key = this.getInvoiceKrollMatchKey(item);
      if (!krollByKey[key]) {
        krollByKey[key] = [];
      }
      krollByKey[key].push(item);
    }

    const keys: { [key: string]: boolean } = {};
    const rows: InvoiceKrollCompareRow[] = [];

    for (let i = 0; i < invoiceItems.length; i++) {
      keys[this.getInvoiceKrollMatchKey(invoiceItems[i])] = true;
    }
    for (const key in krollByKey) {
      if (krollByKey.hasOwnProperty(key)) {
        keys[key] = true;
      }
    }

    for (const key in keys) {
      if (!keys.hasOwnProperty(key)) {
        continue;
      }
      const invoiceItem = invoiceItems.find(item => this.getInvoiceKrollMatchKey(item) === key);
      const krollGroup = krollByKey[key] || [];
      const krollLead = krollGroup.length ? krollGroup[0] : null;
      const source = invoiceItem || krollLead;
      if (!source) {
        continue;
      }

      const invoicePays = invoiceItem != null ? Number(invoiceItem.pays) : null;
      const krollPays = krollGroup.length ? this.resolveKrollPaysForGroup(krollGroup) : null;
      const paysMatch = invoicePays != null && krollPays != null
        && this.compareAmountsEqual(invoicePays, krollPays);

      const cost = invoiceItem != null ? Number(invoiceItem.cost) : Number(krollLead.cost);
      const fee = invoiceItem != null ? Number(invoiceItem.fee) : Number(krollLead.fee);
      const total = invoiceItem != null ? Number(invoiceItem.total) : Number(krollLead.total);
      const krollCost = krollLead != null ? Number(krollLead.cost) : null;
      const krollFee = krollLead != null ? Number(krollLead.fee) : null;
      const krollTotal = krollLead != null ? Number(krollLead.total) : null;
      const invoiceTaxable = invoiceItem != null ? Boolean(invoiceItem.taxable) : null;
      const krollTaxable = krollLead != null ? Boolean(krollLead.taxable) : null;
      const taxable = invoiceTaxable != null ? invoiceTaxable : (krollTaxable != null ? krollTaxable : false);
      const taxableDiffers = invoiceTaxable != null && krollTaxable != null && invoiceTaxable !== krollTaxable;
      const krollHst = this.resolveOntarioHst(krollPays, krollTaxable);
      const invoiceHst = this.resolveOntarioHst(invoicePays, invoiceTaxable);
      const hstMatch = invoiceHst != null && krollHst != null
        && this.compareAmountsEqual(invoiceHst, krollHst);

      rows.push({
        matchKey: key,
        fillDate: source.fillDate,
        origRxNum: source.origRxNum,
        rxNum: source.rxNum,
        quantity: source.quantity,
        drug: source.drug,
        din: source.din,
        cost: cost,
        fee: fee,
        total: total,
        subPlanCode: this.resolveKrollSubPlanCodes(krollGroup),
        taxable: taxable,
        invoiceTaxable: invoiceTaxable,
        krollTaxable: krollTaxable,
        taxableDiffers: taxableDiffers,
        reason: invoiceItem != null && invoiceItem.reason ? invoiceItem.reason : '',
        invoicePays: invoicePays,
        krollPays: krollPays,
        krollHst: krollHst,
        invoiceHst: invoiceHst,
        paysDiff: invoicePays != null && krollPays != null
          ? Number((invoicePays - krollPays).toFixed(2))
          : null,
        hstDiff: invoiceHst != null && krollHst != null
          ? Number((invoiceHst - krollHst).toFixed(2))
          : null,
        paysMatch: paysMatch,
        hstMatch: hstMatch,
        inInvoiceOnly: invoiceItem != null && krollGroup.length === 0,
        inKrollOnly: invoiceItem == null && krollGroup.length > 0,
        costDiffers: invoiceItem != null && krollLead != null && !this.compareAmountsEqual(cost, krollCost),
        feeDiffers: invoiceItem != null && krollLead != null && !this.compareAmountsEqual(fee, krollFee),
        totalDiffers: invoiceItem != null && krollLead != null && !this.compareAmountsEqual(total, krollTotal),
        krollCost: krollCost,
        krollFee: krollFee,
        krollTotal: krollTotal,
      });
    }

    rows.sort((a, b) => {
      const dinA = a.din != null ? String(a.din) : '';
      const dinB = b.din != null ? String(b.din) : '';
      const dinCmp = dinA.localeCompare(dinB, undefined, { numeric: true, sensitivity: 'base' });
      if (dinCmp !== 0) {
        return dinCmp;
      }
      const dateA = this.formatFillDateKey(a.fillDate);
      const dateB = this.formatFillDateKey(b.fillDate);
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }
      return Number(a.rxNum) - Number(b.rxNum);
    });

    this._invoiceKrollCompareRows = rows;
    if (this.sortColumnCompare) {
      this.applySort(this.sortColumnCompare, 'compare', this.sortDirectionCompare);
    }
  }

  private getInvoiceKrollMatchKey(item: any): string {
    return String(item.rxNum) + '|' + this.formatFillDateKey(item.fillDate);
  }

  private formatFillDateKey(fillDate: any): string {
    if (!fillDate) {
      return '';
    }
    const formatted = this.datepipe.transform(fillDate, 'yyyy-MM-dd');
    return formatted ? formatted : String(fillDate);
  }

  private compareAmountsEqual(a: number, b: number): boolean {
    return Math.abs(Number(a) - Number(b)) < 0.005;
  }

  /**
   * Ontario HST on patient Pays: taxable amount × 13%, rounded to 2 decimals
   * (same approach as invoice HST / InvoiceDto.Hst).
   */
  public resolveOntarioHst(pays: number | null, taxable: boolean | null): number | null {
    if (pays == null) {
      return null;
    }
    if (taxable === false) {
      return 0;
    }
    return Math.round(Number(pays) * 0.13 * 100) / 100;
  }

  /** Matched Kroll line Taxable for an invoice item (Rx # + fill date). */
  public getMatchedKrollTaxable(invoiceItem: any): boolean | null {
    const krollItem = this.findMatchedKrollItem(invoiceItem);
    if (!krollItem) {
      return null;
    }
    return Boolean(krollItem.taxable);
  }

  /** True when Invoice Taxable differs from matched Kroll (e.g. OTC force False). */
  public isInvoiceTaxableChangedFromKroll(invoiceItem: any): boolean {
    const krollTaxable = this.getMatchedKrollTaxable(invoiceItem);
    if (krollTaxable == null || invoiceItem == null) {
      return false;
    }
    return Boolean(invoiceItem.taxable) !== krollTaxable;
  }

  private findMatchedKrollItem(invoiceItem: any): any {
    if (!invoiceItem || !this.invoiceKroll || !this.invoiceKroll.invoiceItemDtos) {
      return null;
    }
    const key = this.getInvoiceKrollMatchKey(invoiceItem);
    for (let i = 0; i < this.invoiceKroll.invoiceItemDtos.length; i++) {
      const krollItem = this.invoiceKroll.invoiceItemDtos[i];
      if (this.getInvoiceKrollMatchKey(krollItem) === key) {
        return krollItem;
      }
    }
    return null;
  }

  /** Sum patient Pays on Kroll lines for the same Rx (Cash plan lines when present). */
  private resolveKrollPaysForGroup(krollItems: any[]): number {
    if (!krollItems || !krollItems.length) {
      return 0;
    }
    const cashLines = krollItems.filter(item =>
      item.subPlanCode && String(item.subPlanCode).indexOf('Cash') >= 0);
    const lines = cashLines.length ? cashLines : krollItems;
    let sum = 0;
    for (let i = 0; i < lines.length; i++) {
      sum += Number(lines[i].pays || 0);
    }
    return Number(sum.toFixed(2));
  }

  private resolveKrollSubPlanCodes(krollItems: any[]): string {
    if (!krollItems || !krollItems.length) {
      return '';
    }
    const unique: string[] = [];
    for (let i = 0; i < krollItems.length; i++) {
      const code = krollItems[i].subPlanCode;
      if (code && unique.indexOf(code) < 0) {
        unique.push(code);
      }
    }
    return unique.join(' / ');
  }

  /** Shows SweetAlert2 confirmation for payment review status change (FLAG/APPROVE/PENDING/FORCE PENDING). */
  showPaymentReviewStatusChangeConfirm(status: number, forcePending: boolean = false): void {
    if (this.shouldBlockPaymentReviewStatusChange(status, forcePending)) {
      return;
    }

    const config = this.getPaymentReviewStatusChangeConfig(status, forcePending);
    const self = this;

    Swal.fire({
      icon: config.icon,
      title: config.title,
      html: config.html,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: config.confirmButtonText,
      confirmButtonColor: config.confirmButtonColor,
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    }).then(function (result) {
      if (!result || !result.isConfirmed) {
        return;
      }
      self.savePaymentReviewStatus(status, forcePending);
    });
  }

  showPaymentStatusChangeConfirm(status: number): void {
    this.showPaymentReviewStatusChangeConfirm(status);
  }

  openPaymentReviewStatusDialog(status: number): void {
    this.showPaymentReviewStatusChangeConfirm(status);
  }

  private shouldBlockPaymentReviewStatusChange(status: number, forcePending: boolean = false): boolean {
    if (status === REVIEW_STATUS.FLAGGED && this.disablePaymentFlagButton) {
      return true;
    }
    if (status === REVIEW_STATUS.APPROVED && this.disablePaymentApproveButton) {
      return true;
    }
    if (status === REVIEW_STATUS.PENDING) {
      if (forcePending) {
        return this.disablePaymentForcePendingButton;
      }
      return this.disablePaymentPendingButton;
    }
    return false;
  }

  private getPaymentReviewStatusChangeConfig(status: number, forcePending: boolean = false): any {
    const patientName = this.invoice && this.invoice.billedTo ? this.invoice.billedTo : 'this';
    if (status === REVIEW_STATUS.PENDING && forcePending) {
      return {
        icon: 'warning' as const,
        title: 'Force Payment Review to Pending',
        html: '<p>Force payment review to <strong>Pending</strong> on <strong>' + patientName
          + '</strong> invoice?</p><p class="text-muted small mb-0">Sets payment status to <strong>Unpaid</strong> and balances portal payment rows so purchase can run again after a Helcim refund. Use only for recovery (risk of double charge).</p>',
        confirmButtonText: 'Yes, force pending',
        confirmButtonColor: '#dc3545'
      };
    }
    const configs = {
      1: {
        icon: 'question' as const,
        title: 'Set Payment Review to Pending',
        html: '<p>Are you sure you want to set payment review to <strong>Pending</strong> on <strong>' + patientName + '</strong> invoice?</p>',
        confirmButtonText: 'Yes, set to pending',
        confirmButtonColor: '#ffc107'
      },
      2: {
        icon: 'warning' as const,
        title: 'Set Payment Review to Flagged',
        html: '<p>Are you sure you want to flag payment review on <strong>' + patientName + '</strong> invoice?</p>',
        confirmButtonText: 'Yes, flag it',
        confirmButtonColor: '#dc3545'
      },
      3: {
        icon: 'question' as const,
        title: 'Set Payment Review to Approved',
        html: '<p>Are you sure you want to approve payment review on <strong>' + patientName + '</strong> invoice?</p>',
        confirmButtonText: 'Yes, approve it',
        confirmButtonColor: '#28a745'
      }
    };
    return configs[status] || configs[1];
  }

  private savePaymentReviewStatus(status: number, forcePending: boolean = false): void {
    if (!this.invoice || !this.invoice.id) {
      return;
    }

    this.showLoadingMessage = true;
    this.invoiceService.updateInvoicePaymentReviewStatus(this.invoice.id, status, forcePending).subscribe(
      function (result) {
        this.showLoadingMessage = false;
        if (!result || !result.success || !result.data) {
          this.loadErrorMessage(result && result.data ? result.data : 'Unable to update payment review status.');
          return;
        }

        this.sharedService.showSuccessMessage = true;
        setTimeout(function () { this.sharedService.showSuccessMessage = false; }.bind(this), 3000);
        // Same as Refresh Page after Payment review actions.
        this.loadFields();
      }.bind(this),
      function (err) {
        this.showLoadingMessage = false;
        const payload = (err && err.error) ? err.error : ((err && err.data) ? err.data : err);
        this.loadErrorMessage(payload);
      }.bind(this)
    );
  }

  openPurchaseDialog(): void {
    if (this.disablePurchaseButton) {
      this.showPurchaseUnavailableSwal('Invoice is not available for payment purchase.');
      return;
    }
    this.openPaymentDialog();
  }

  openPaymentDialog(): void {
    if (!this.invoice) {
      return;
    }

    if (!this.hasPersistedInvoiceId()) {
      Swal.fire({
        icon: 'warning',
        title: 'Purchase',
        text: 'Invoice must be saved before purchase.',
        confirmButtonText: 'OK',
      });
      return;
    }

    const invoiceNumber = this.invoice.invoiceNumber != null ? String(this.invoice.invoiceNumber) : '-';
    const billedTo = this.invoice.billedTo != null ? String(this.invoice.billedTo) : '-';
    const invoiceId = String(this.invoice.id);
    const subTotalText = this.formatInvoiceCurrency(this.subTotal);
    const hstText = this.formatInvoiceCurrency(this.hst);
    const totalText = this.formatInvoiceCurrency(this.invoice.total);

    Swal.fire({
      icon: 'question',
      title: 'Purchase',
      html: ''
        + '<div style="max-width: 300px; margin: 0 auto; text-align: left;">'
        + '<table style="width: 100%; border-collapse: collapse; margin-bottom: 14px;">'
        + '<tr><td style="padding: 3px 12px 3px 0; white-space: nowrap; vertical-align: top;"><strong>Resident:</strong></td>'
        + '<td style="padding: 3px 0;">' + billedTo + '</td></tr>'
        + '<tr><td style="padding: 3px 12px 3px 0; white-space: nowrap;"><strong>InvoiceNumber:</strong></td>'
        + '<td style="padding: 3px 0;">' + invoiceNumber + '</td></tr>'
        + '<tr><td style="padding: 3px 12px 3px 0; white-space: nowrap;"><strong>InvoiceId:</strong></td>'
        + '<td style="padding: 3px 0;">' + invoiceId + '</td></tr>'
        + '</table>'
        + '<table style="width: 100%; border-collapse: collapse; margin-bottom: 14px;">'
        + '<tr><td style="padding: 4px 20px 4px 0;">SubTotal</td>'
        + '<td style="padding: 4px 0; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap;">' + subTotalText + '</td></tr>'
        + '<tr><td style="padding: 4px 20px 4px 0;">HST</td>'
        + '<td style="padding: 4px 0; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap;">' + hstText + '</td></tr>'
        + '<tr><td colspan="2" style="padding: 0; border-top: 1px solid #dee2e6;"></td></tr>'
        + '<tr><td style="padding: 8px 20px 4px 0;"><strong>Total</strong></td>'
        + '<td style="padding: 8px 0 4px 0; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap;"><strong>' + totalText + '</strong></td></tr>'
        + '</table>'
        + '<p class="text-muted mb-0" style="text-align: center;">Continue to open the payment purchase link.</p>'
        + '</div>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Continue',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
    }).then((result) => {
      if (!result || !result.isConfirmed) {
        return;
      }

      const purchaseWindow = this.openCenteredPopup(
        'about:blank',
        'solvoPaymentPurchase',
        520,
        850);
      if (!purchaseWindow) {
        this.loadErrorMessage('Please allow pop-ups to open the secure payment page.');
        return;
      }

      this.showLoadingMessage = true;
      this.invoiceService.generatePurchaseLink(this.invoice.id).subscribe(
        (response) => {
          this.showLoadingMessage = false;
          if (!response || !response.url) {
            if (purchaseWindow) {
              purchaseWindow.close();
            }
            this.showPurchaseUnavailableSwal('Payment purchase link was not returned.');
            return;
          }

          purchaseWindow.location.href = response.url;
          this.watchPurchasePopupThenRefresh(purchaseWindow);
        },
        (err) => {
          this.showLoadingMessage = false;
          if (purchaseWindow) {
            purchaseWindow.close();
          }
          const payload = (err && err.error) ? err.error : ((err && err.data) ? err.data : err);
          this.showPurchaseUnavailableSwal(payload);
        });
    });
  }

  private showPurchaseUnavailableSwal(payload: any): void {
    const self = this;
    Swal.fire({
      icon: 'error',
      title: 'Purchase',
      text: formatOopsErrorMessage(payload),
      confirmButtonText: 'OK'
    }).then(function (result) {
      if (result && result.isConfirmed) {
        self.loadFields();
      }
    });
  }

  private openCenteredPopup(url: string, name: string, width: number, height: number): Window {
    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : (window as any).screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : (window as any).screenY;
    const screenWidth = window.innerWidth
      ? window.innerWidth
      : (document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width);
    const screenHeight = window.innerHeight
      ? window.innerHeight
      : (document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height);
    const left = Math.max(0, ((screenWidth - width) / 2) + dualScreenLeft);
    const top = Math.max(0, ((screenHeight - height) / 2) + dualScreenTop);
    const features = 'scrollbars=yes,resizable=yes,width=' + width
      + ',height=' + height
      + ',top=' + Math.round(top)
      + ',left=' + Math.round(left);
    const popup = window.open(url, name, features);
    if (popup && typeof popup.focus === 'function') {
      popup.focus();
    }
    return popup;
  }

  openForceCompletePaymentDialog(): void {
    if (this.disableForceCompletePaymentButton) {
      return;
    }

    const invoiceNumber = this.invoice.invoiceNumber != null ? String(this.invoice.invoiceNumber) : '-';
    const invoiceId = String(this.invoice.id);
    const paymentIdText = this.forceCompletePaymentId != null ? String(this.forceCompletePaymentId) : '-';
    const amountText = this.forceCompleteAmount != null
      ? this.formatInvoiceCurrency(this.forceCompleteAmount)
      : '-';
    const self = this;

    Swal.fire({
      icon: 'warning',
      title: 'Force Create Payment',
      html: ''
        + '<p class="mb-2 text-start">Create the Portal payment record from the verified SolvoPayment purchase. '
        + 'If the Portal row already exists, it will be reused.</p>'
        + '<p class="mb-1 text-start"><strong>InvoiceNumber:</strong> ' + invoiceNumber + '</p>'
        + '<p class="mb-1 text-start"><strong>InvoiceId:</strong> ' + invoiceId + '</p>'
        + '<p class="mb-1 text-start"><strong>PaymentId:</strong> ' + paymentIdText + '</p>'
        + '<p class="mb-3 text-start"><strong>Amount:</strong> ' + amountText + '</p>'
        + '<p class="mb-0 text-start text-muted small">A PENDING purchase link is used when available; reuse does not require one.</p>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Force Create Payment',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    }).then(function (result) {
      if (!result || !result.isConfirmed) {
        return;
      }
      self.forceCompletePurchase();
    });
  }

  openRunScheduledChargeDialog(): void {
    if (this.disableRunScheduledChargeButton) {
      return;
    }

    const invoiceNumber = this.invoice.invoiceNumber != null ? String(this.invoice.invoiceNumber) : '-';
    const invoiceId = String(this.invoice.id);
    const billedTo = this.invoice.billedTo != null ? String(this.invoice.billedTo) : '-';
    const self = this;

    Swal.fire({
      icon: 'warning',
      title: 'Run Scheduled Charge (QA)',
      html: ''
        + '<p class="mb-2 text-start">Runs the <strong>same scheduled-charge path</strong> as the Scheduler batch '
        + 'for this invoice only (MethodOrder card → bank). Sends the <strong>ACP Scheduled charges</strong> staff email.</p>'
        + '<p class="mb-1 text-start"><strong>Invoice #</strong> ' + invoiceNumber + '</p>'
        + '<p class="mb-1 text-start"><strong>Invoice Id</strong> ' + invoiceId + '</p>'
        + '<p class="mb-3 text-start"><strong>Resident</strong> ' + billedTo + '</p>'
        + '<p class="mb-0 text-start text-muted small">QA only. Eligible invoices: Payment Review Approved, net unpaid, no Sage BF receipt.</p>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Run Scheduled Charge',
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      focusCancel: true
    }).then(function (result) {
      if (!result || !result.isConfirmed) {
        return;
      }
      self.runScheduledCharge();
    });
  }

  openAdminRefundPaymentDialog(): void {
    if (this.disableAdminRefundPaymentButton) {
      return;
    }

    const invoiceNumber = this.invoice.invoiceNumber != null ? String(this.invoice.invoiceNumber) : '-';
    const invoiceId = String(this.invoice.id);
    const billedTo = this.invoice.billedTo != null ? String(this.invoice.billedTo) : '-';
    const paymentAmountText = this.invoice.paymentAmount != null
      ? this.formatInvoiceCurrency(this.invoice.paymentAmount)
      : '-';
    const refundAmountText = this.invoice.paymentRefundAmount != null
      ? this.formatInvoiceCurrency(this.invoice.paymentRefundAmount)
      : this.formatInvoiceCurrency(0);
    const self = this;

    Swal.fire({
      icon: 'warning',
      title: 'Refund Payment — permanent action',
      html: ''
        + '<p class="mb-2 text-start text-danger"><strong>Warning:</strong> This refunds the Helcim / SolvoPayment charge and updates Portal. '
        + 'It cannot be undone from SolvoPortal. Money may return to the customer card or bank account.</p>'
        + '<p class="mb-1 text-start"><strong>Invoice #</strong> ' + invoiceNumber + '</p>'
        + '<p class="mb-1 text-start"><strong>Invoice Id</strong> ' + invoiceId + '</p>'
        + '<p class="mb-1 text-start"><strong>Resident</strong> ' + billedTo + '</p>'
        + '<p class="mb-1 text-start"><strong>Paid</strong> ' + paymentAmountText + '</p>'
        + '<p class="mb-3 text-start"><strong>Already refunded</strong> ' + refundAmountText + '</p>'
        + '<p class="mb-0 text-start text-muted small">Full remaining balance will be refunded. If Sage 50 already has a Receive Payment, reverse it manually in Sage 50 Finance.</p>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, refund payment',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      focusCancel: true
    }).then(function (result) {
      if (!result || !result.isConfirmed) {
        return;
      }
      self.refundInvoicePayment();
    });
  }

  private refundInvoicePayment(): void {
    if (this.disableAdminRefundPaymentButton) {
      return;
    }

    const self = this;
    this.isRefundingPayment = true;
    this.showLoadingMessage = true;
    this.invoiceService.refundInvoicePayment(this.invoice.id)
      .pipe(finalize(function () {
        self.isRefundingPayment = false;
        self.showLoadingMessage = false;
      }))
      .subscribe(
        function () {
          Swal.fire({
            icon: 'success',
            title: 'Refund completed',
            text: 'Helcim / SolvoPayment and Portal payment records were updated.',
            confirmButtonText: 'OK'
          });
          self.reloadInvoiceAfterForceComplete();
        },
        function (err) {
          const detail = err && err.error && (err.error.message || err.error.detail)
            ? String(err.error.message || err.error.detail)
            : 'Refund could not be completed.';
          Swal.fire({
            icon: 'error',
            title: 'Refund failed',
            text: detail,
            confirmButtonText: 'OK'
          });
        });
  }

  private runScheduledCharge(): void {
    if (this.disableRunScheduledChargeButton) {
      return;
    }

    const self = this;
    this.isRunningScheduledCharge = true;
    this.showLoadingMessage = true;
    this.invoiceService.runScheduledCharge(this.invoice.id, true)
      .pipe(finalize(function () {
        self.isRunningScheduledCharge = false;
        self.showLoadingMessage = false;
      }))
      .subscribe(
        function (response) {
          const batch = response && response.data ? response.data : response;
          const items = batch && batch.items ? batch.items
            : (batch && batch.Items ? batch.Items : null);
          const first = items && items.length > 0 ? items[0] : null;
          const status = first
            ? String(first.status != null ? first.status : first.Status || '')
            : '';
          const detail = first
            ? String(first.detail != null ? first.detail : first.Detail || '')
            : '';
          const emailSent = !!(batch && (batch.emailSent === true || batch.EmailSent === true));
          const ok = status === 'Paid'
            || status === 'PendingSettlement'
            || status === 'LinkedExisting';
          Swal.fire({
            icon: ok ? 'success' : (status === 'Skipped' ? 'info' : 'warning'),
            title: ok ? 'Scheduled charge finished' : 'Scheduled charge result',
            html: ''
              + '<p class="mb-1 text-start"><strong>Status:</strong> ' + (status || '—') + '</p>'
              + '<p class="mb-1 text-start"><strong>Detail:</strong> ' + (detail || '—') + '</p>'
              + '<p class="mb-0 text-start"><strong>Email sent:</strong> ' + (emailSent ? 'Yes' : 'No') + '</p>',
            confirmButtonText: 'OK'
          });
          self.reloadInvoiceAfterForceComplete();
        },
        function (err) {
          const detail = err && err.error && (err.error.message || err.error.detail)
            ? String(err.error.message || err.error.detail)
            : 'Scheduled charge could not be completed.';
          Swal.fire({
            icon: 'error',
            title: 'Scheduled charge failed',
            text: detail,
            confirmButtonText: 'OK'
          });
        });
  }

  private refreshForceCompleteStatus(): void {
    this.forceCompleteEligible = false;
    this.forceCompletePaymentId = null;
    this.forceCompleteAmount = null;

    if (!this.hasInvoiceAdminPrivilege || !this.hasPersistedInvoiceId()) {
      return;
    }

    const self = this;
    this.forceCompleteStatusLoading = true;
    this.invoiceService.getForceCompletePurchaseStatus(this.invoice.id)
      .pipe(finalize(function () {
        self.forceCompleteStatusLoading = false;
      }))
      .subscribe(
        function (status) {
          if (!status || status.eligible !== true) {
            self.forceCompleteEligible = false;
            return;
          }
          self.forceCompleteEligible = true;
          self.forceCompletePaymentId = status.paymentId != null ? Number(status.paymentId) : null;
          self.forceCompleteAmount = status.amount != null ? Number(status.amount) : null;
        },
        function () {
          self.forceCompleteEligible = false;
        });
  }

  private forceCompletePurchase(): void {
    if (this.disableForceCompletePaymentButton) {
      return;
    }

    const self = this;
    this.isForceCompletingPurchase = true;
    this.showLoadingMessage = true;
    this.invoiceService.forceCompletePurchase(this.invoice.id)
      .pipe(finalize(function () {
        self.isForceCompletingPurchase = false;
        self.showLoadingMessage = false;
      }))
      .subscribe(
        function (response) {
          const data = response && response.data ? response.data : response;
          const reused = !!(data && (data.reused === true || data.Reused === true));
          if (reused) {
            Swal.fire({
              icon: 'success',
              title: 'Payment already linked',
              text: 'The Portal InvoicePayment row already existed and was reused.',
              confirmButtonColor: '#28a745'
            });
          } else {
            self.sharedService.showSuccessMessage = true;
            setTimeout(function () { self.sharedService.showSuccessMessage = false; }, 3000);
          }
          self.reloadInvoiceAfterForceComplete();
        },
        function (err) {
          const payload = (err && err.error) ? err.error : ((err && err.data) ? err.data : err);
          self.loadErrorMessage(payload);
          self.refreshForceCompleteStatus();
        });
  }

  /** Same as clicking Refresh Page after Admin / payment recovery actions. */
  private reloadInvoiceAfterForceComplete(): void {
    this.loadFields();
  }

  /** When the purchase popup closes, refresh detail like Refresh Page. */
  private watchPurchasePopupThenRefresh(popup: Window): void {
    if (!popup) {
      return;
    }
    const self = this;
    const timer = window.setInterval(function () {
      if (!popup.closed) {
        return;
      }
      window.clearInterval(timer);
      self.loadFields();
    }, 800);
  }

  private formatInvoiceCurrency(amount: any): string {
    const value = Number(amount);
    if (isNaN(value)) {
      return '$0.00';
    }
    return '$' + value.toFixed(2);
  }

  createSalesInvoice(): void {
    if (!this.invoice || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED) return;
    if (this.showLoadingMessage) return;

    this.ensureSageCustomerProfileThen(() => this.confirmSalesInvoiceFlow('create'));
  }

  updateSalesInvoice(): void {
    if (!this.invoice || this.invoice.invoiceReviewStatusId !== REVIEW_STATUS.APPROVED) return;
    if (this.showLoadingMessage) return;
    if (!this.hasPersistedInvoiceId()) {
      Swal.fire({
        icon: 'warning',
        title: 'Invoice number required',
        text: 'Cannot update Sage invoice without an invoice number.',
      });
      return;
    }

    this.ensureSageCustomerProfileThen(() => this.confirmSalesInvoiceFlow('update'));
  }

  /** Loads Sage profile; runs callback only when Sage customer id is present. */
  private ensureSageCustomerProfileThen(onReady: () => void): void {
    this.invoiceService.getInvoiceSageProfile(this.pharmacyId, this.facilityId, this.patientId).subscribe(
      res => {
        const profile = res && res.data;
        if (profile == null || profile.sageCustomerId == null || profile.sageCustomerId === undefined) {
          Swal.fire({
            icon: 'warning',
            title: 'Update Sage Profile Required',
            text: 'Please update Sage Profile.',
          });
          return;
        }
        onReady();
      },
      () => {
        Swal.fire({
          icon: 'warning',
          title: 'Update Sage Profile Required',
          text: 'Please update Sage Profile.',
        });
      }
    );
  }

  /** Create uses portal → Sage POST v1/invoices/create; update uses POST v1/invoices/customers/{customerId}/transactions/{transactionId}/update. */
  private confirmSalesInvoiceFlow(mode: 'create' | 'update'): void {
    const inv = this.invoice;
    const startDateStr = this.invoice.startDate ? this.datepipe.transform(this.invoice.startDate, 'yyyy-MM-dd') : '';
    const isUpdate = mode === 'update';

    Swal.fire({
      icon: 'question',
      title: isUpdate ? 'Update Sales Invoice' : 'Create Sales Invoice',
      html: `
        <div class="text-start">
          <p class="mb-3">${isUpdate
            ? 'Do you want to update the sales invoice with the following details?'
            : 'Do you want to create a sales invoice with the following details?'}</p>
          <div class="mb-3">
            <strong>Resident Name:</strong> ${inv.billedTo || '-'}<br>
            <strong>Invoice ID:</strong> ${inv.id}<br>
            <strong>Invoice Number:</strong> ${inv.invoiceNumber || '-'}<br>
            <strong>Invoice Date:</strong> ${startDateStr}
          </div>
          <table class="table table-sm table-borderless mb-0">
            <tbody>
              <tr>
                <td>Taxable:</td>
                <td class="text-end">$${(inv.subTotalTaxableItems != null ? inv.subTotalTaxableItems : 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td>Non-Taxable:</td>
                <td class="text-end">$${(inv.subTotalNonTaxableItems != null ? inv.subTotalNonTaxableItems : 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td>HST:</td>
                <td class="text-end">$${(inv.hst != null ? inv.hst : 0).toFixed(2)}</td>
              </tr>
              <tr class="border-top">
                <td><strong>TOTAL:</strong></td>
                <td class="text-end"><strong>$${(inv.total != null ? inv.total : 0).toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: isUpdate ? 'Yes, update sales invoice' : 'Yes, create sales invoice',
    }).then(result => {
      if (!result.isConfirmed) return;

      this.showLoadingMessage = true;
      Swal.fire({
        title: isUpdate ? 'Updating sales invoice...' : 'Creating sales invoice...',
        text: 'Sage invoice and balance-forward sync can take 10–30 seconds. Please wait.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        onOpen: () => { Swal.showLoading(); },
      });

      const request$ = isUpdate
        ? this.invoiceService.updateSalesInvoice(this.pharmacyId, this.facilityId, this.patientId, this.invoice.id)
        : this.invoiceService.createSalesInvoice(this.pharmacyId, this.facilityId, this.patientId, this.invoice.id);

      request$
        .pipe(
          timeout(90000),
          finalize(() => {
            Swal.close();
            this.showLoadingMessage = false;
          })
        )
        .subscribe(
          res => {
            if (!res.success) {
              this.loadErrorMessage(res.data);
              return;
            }
            this.sharedService.showSuccessMessage = true;
            setTimeout(() => { this.sharedService.showSuccessMessage = false; }, 5000);
            this.loadFields();
          },
          err => {
            var payload = (err && err.error) ? err.error : ((err && err.data) ? err.data : err);
            this.loadErrorMessage(payload);
          }
        );
    });
  }

  getDinColor(din: any): string {
    if (!din) return 'transparent';
    const dinStr = din.toString();

    const colors = [
      '#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF',
      '#E6B3FF', '#FFDAC1', '#E2F0CB', '#B5EAD7', '#C7CEEA',
      '#F497AD', '#D6AEFF', '#A2E1DB', '#FFD8B1', '#FFABAB',
      '#AFCBFF', '#FFCC99', '#D9A5B3', '#ACE5EE', '#CFDBC5'
    ];

    let hash = 0;
    for (let i = 0; i < dinStr.length; i++) {
      hash = dinStr.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }
}

// Highlight matching text in autocomplete options.
@Pipe({ name: 'highlight' })
export class HighlightPipe implements PipeTransform {
  transform(text: string, search): string {
    if (search && text && typeof search === 'string' && typeof text === 'string') {
      const pattern = search
        .replace(/[\-\[\]\/{}()*x+?.\\^$|]/g, '\\$&')
        .split(' ')
        .filter(t => t.length > 0)
        .join('|');
      const regex = new RegExp(pattern, 'gi');
      return search ? text.replace(regex, match => `<strong class="highlight">${match}</strong>`) : text;
    }
    return text;
  }
}
